import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '@/components/Logo';
import GoogleRatingBadge from '@/components/GoogleRatingBadge';
import { ChevronDown, MenuIcon, CloseIcon, PhoneIcon, ArrowRight } from '@/components/icons';
import { BUSINESS } from '@/lib/site';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/courses', label: 'Courses', dropdown: [
    { to: '/courses/standard', label: 'Standard / Renewal Course' },
    { to: '/courses/beginner', label: 'Beginner Course' },
    { to: '/courses/f-endorsement', label: '“F” Endorsement' },
    { to: '/courses/nzqa', label: 'NZQA Unit Standards' },
    { to: '/courses', label: 'All Courses & Services' },
  ]},
  { to: '/location', label: 'Location' },
  { to: '/booking', label: 'Booking' },
];

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); setCoursesOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    document.body.classList.toggle('menu-open', open);
  }, [open]);

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* Top rating strip */}
      <div className="hi-vis-stripe h-1" aria-hidden="true" />
      <div className="bg-[#060D9C] text-white">
        <div className="nft-container flex items-center justify-between py-2 text-xs">
          <div className="flex items-center gap-3">
            <GoogleRatingBadge compact dark={false} />
            <span className="hidden sm:inline label-eyebrow text-white/70">WorkSafeNZ Approved · Est. 1990</span>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <a href={BUSINESS.phoneHref} className="inline-flex items-center gap-1.5 hover:text-[#FC4E2C] transition-colors" data-testid="top-strip-phone">
              <PhoneIcon width={14} height={14} /> {BUSINESS.phone}
            </a>
            <span className="opacity-60">|</span>
            <span className="label-eyebrow text-white/70">{BUSINESS.hours}</span>
          </div>
          <div className="md:hidden">
            <a href={BUSINESS.phoneHref} className="inline-flex items-center gap-1.5 text-[#FC4E2C] font-bold">
              <PhoneIcon width={14} height={14} /> Call
            </a>
          </div>
        </div>
      </div>

      {/* Main nav bar */}
      <div className={`bg-white border-b transition-shadow ${scrolled ? 'shadow-md border-transparent' : 'border-[rgba(26,36,181,0.18)]'}`}>
        <div className="nft-container flex items-center justify-between py-3">
          <Logo />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV_LINKS.map((link) => link.dropdown ? (
              <div key={link.to} className="relative"
                onMouseEnter={() => setCoursesOpen(true)}
                onMouseLeave={() => setCoursesOpen(false)}
              >
                <button
                  data-testid="site-header-courses-dropdown-trigger"
                  className="px-3 py-2 inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-[#060D9C] hover:text-[#FC4E2C] transition-colors"
                  style={{ fontFamily: 'Barlow Semi Condensed' }}
                  onClick={() => setCoursesOpen(v => !v)}
                  aria-expanded={coursesOpen}
                >
                  {link.label} <ChevronDown width={14} height={14} />
                </button>
                <AnimatePresence>
                  {coursesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 min-w-[280px] bg-white border border-[rgba(26,36,181,0.18)] rounded-xl shadow-xl py-2"
                    >
                      {link.dropdown.map((d) => (
                        <NavLink
                          key={d.to}
                          to={d.to}
                          end={d.to === '/courses'}
                          data-testid={`courses-dropdown-link-${d.to.replace(/\//g,'-')}`}
                          className={({isActive}) => `block px-4 py-2.5 text-sm font-bold transition-colors ${isActive ? 'text-[#FC4E2C] bg-[rgba(252,78,44,0.06)]' : 'text-[#060D9C] hover:text-[#FC4E2C] hover:bg-[rgba(252,78,44,0.04)]'}`}
                          style={{ fontFamily: 'Barlow Semi Condensed' }}
                        >
                          {d.label}
                        </NavLink>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink
                key={link.to}
                to={link.to}
                end
                data-testid={`site-header-link-${link.label.toLowerCase()}`}
                className={({isActive}) => `relative px-3 py-2 text-sm font-bold uppercase tracking-wide transition-colors ${isActive ? 'text-[#FC4E2C]' : 'text-[#060D9C] hover:text-[#FC4E2C]'}`}
                style={{ fontFamily: 'Barlow Semi Condensed' }}
              >
                {({isActive}) => (
                  <>
                    {link.label}
                    {isActive && (
                      <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-[#FC4E2C]" aria-hidden="true" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link to="/booking" className="btn-primary-orange" data-testid="site-header-booking-link">
              Book a Course <ArrowRight width={16} height={16} />
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 rounded-lg border border-[rgba(26,36,181,0.18)] text-[#060D9C]"
            onClick={() => setOpen(true)}
            data-testid="site-header-mobile-menu-button"
            aria-label="Open menu"
          >
            <MenuIcon />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[60] lg:hidden"
            data-testid="mobile-menu-overlay"
          >
            <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.25 }}
              className="absolute right-0 top-0 bottom-0 w-[88%] max-w-[420px] bg-[#060D9C] text-white flex flex-col"
            >
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <Logo variant="dark" compact />
                <button
                  className="p-2 rounded-lg border border-white/20"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-menu-close"
                  aria-label="Close menu"
                >
                  <CloseIcon />
                </button>
              </div>
              <div className="p-4 border-b border-white/10">
                <GoogleRatingBadge dark={false} />
              </div>
              <nav className="flex-1 overflow-y-auto p-4" aria-label="Mobile primary">
                {NAV_LINKS.map((link) => (
                  <div key={link.to}>
                    <NavLink
                      to={link.to}
                      end
                      className={({isActive}) => `block py-3 text-2xl font-display font-extrabold uppercase border-b border-white/10 transition-colors ${isActive ? 'text-[#FC4E2C]' : 'text-white hover:text-[#FC4E2C]'}`}
                      data-testid={`mobile-link-${link.label.toLowerCase()}`}
                    >
                      {link.label}
                    </NavLink>
                    {link.dropdown && (
                      <div className="pl-3 py-2 space-y-1 border-b border-white/10">
                        {link.dropdown.map(d => (
                          <NavLink
                            key={d.to}
                            to={d.to}
                            className="block py-1.5 text-sm text-white/80 hover:text-[#FC4E2C]"
                            style={{ fontFamily: 'Barlow Semi Condensed' }}
                          >
                            → {d.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
              <div className="p-4 border-t border-white/10 flex flex-col gap-2">
                <Link to="/booking" className="btn-primary-orange w-full justify-center">
                  Book a Course <ArrowRight width={16} height={16} />
                </Link>
                <a href={BUSINESS.phoneHref} className="btn-outline-white w-full justify-center">
                  <PhoneIcon width={16} height={16} /> {BUSINESS.phone}
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
